<template>
  <div
    class="report-loader__wrapper"
  >
    <div class="report-loader-info__wrapper">
      <GlBlockchainIcon
        v-if="coinData?.key"
        :blockchain-icon="coinData?.key"
      />
      <div class="report-loader-info__label">
        Generating Report
      </div>
      <div class="report-loader-info__wallet">
        {{ isMobile ? trancateString(walletAddress, 10) : walletAddress }}
      </div>
    </div>
    <div class="report-loader-check__wrapper">
      <div class="report-loader-check__equalizer">
        <GlBarEqualizer
          :key="isMobile"
          :total-bars="isMobile ? 30 : 40"
          :value="progressWidth"
        />
      </div>
      <div class="report-loader-check__container">
        <transition
          mode="out-in"
          name="fade-slide"
        >
          <div
            v-if="stage === 0"
            :key="stage"
          >
            Checking wallet address details...
          </div>
          <div
            v-else-if="stage === 1"
            :key="stage"
          >
            <gl-icon
              brand
              :height="24"
              name="I-Search"
              :width="24"
            /> Scanning
            wallet for connections to
            <span class="highlight">scams or hacks...</span>
          </div>
          <div
            v-else-if="stage === 2"
            :key="stage"
          >
            <gl-icon
              brand
              :height="24"
              name="I-Search"
              :width="24"
            />
            Calculating wallet <span class="highlight">risk score...</span>
          </div>
          <div
            v-else-if="stage === 3"
            :key="stage"
          >
            <gl-icon
              brand
              :height="24"
              name="I-Search"
              :width="24"
            /> Scanning
            wallet for ties to
            <span class="highlight">risky entities...</span>
          </div>
          <div
            v-else-if="stage === 4"
            :key="stage"
          >
            <gl-icon
              brand
              :height="24"
              name="I-Search"
              :width="24"
            /> Checking
            where the
            <span
              class="highlight"
            >funds came from and where they went...</span>
          </div>
          <div
            v-else-if="stage === 5"
            :key="stage"
          >
            <gl-icon
              brand
              :height="24"
              name="I-Done-Fill"
              :width="24"
            />
            <span class="highlight"> All transaction and entity data</span>
            associated with this address have been collected
          </div>
          <div v-else>
            Processing...
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
// components
import GlIcon from '@/components/gl-icon'
import GlBarEqualizer from '@/components/loaders/gl-equalizer-loader'
import GlBlockchainIcon from '@/components/gl-blockchain-icon'
// Vuex
import { mapState, mapGetters, mapMutations } from 'vuex'
// utils
import { trancateString } from '@/utils/text-formatter'
//mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

export default {
  name: 'GenerateReportLoader',
  components: { GlBarEqualizer, GlIcon, GlBlockchainIcon },
  mixins: [deviceWidthMixin],
  props: {
    isFirstGenerateLoader: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      stage: 0,
      stageCount: 5,
      intervalId: null,
      shouldContinueAfterWait: false,
    }
  },
  computed: {
    ...mapState('analytics', ['coinData']),
    ...mapGetters('report', [
      'getIsGenerateReportLoaderVisible',
      'getReportHeaderData',
    ]),
    progressWidth() {
      if (this.stage == 0) return 3
      return Math.floor((this.stage / this.stageCount) * 100)
    },
    walletAddress() {
      const { query } = this.$route
      return query?.address
    },
  },
  watch: {
    isFirstGenerateLoader: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startProgress()
        } else if (this.shouldContinueAfterWait) {
          this.shouldContinueAfterWait = false
          this.continueProgress()
        }
      },
    },
  },
  beforeDestroy() {
    this.SET_IS_GENERATE_REPORT_LOADER_VISIBLE(false)
    clearInterval(this.intervalId)
  },
  methods: {
    ...mapMutations('report', ['SET_IS_GENERATE_REPORT_LOADER_VISIBLE']),
    trancateString,
    async startProgress() {
      if (this.getIsGenerateReportLoaderVisible) return
      this.SET_IS_GENERATE_REPORT_LOADER_VISIBLE(true)
      await new Promise(resolve => setTimeout(resolve, 1000))

      this.stage = 1
      clearInterval(this.intervalId)
      this.intervalId = setInterval(() => {
        if (this.stage === this.stageCount - 1 && this.isFirstGenerateLoader) {
          this.shouldContinueAfterWait = true
          clearInterval(this.intervalId)
          return
        }

        this.stage++

        if (this.stage >= this.stageCount) {
          this.finishProgress()
        }
      }, 1000)
    },
    continueProgress() {
      clearInterval(this.intervalId)

      this.intervalId = setInterval(() => {
        this.stage++

        if (this.stage >= this.stageCount) {
          this.finishProgress()
        }
      }, 1000)
    },
    finishProgress() {
      clearInterval(this.intervalId)
      this.intervalId = null

      setTimeout(() => {
        this.SET_IS_GENERATE_REPORT_LOADER_VISIBLE(false)
        this.stage = 0
      }, 1400)
    },
  },
}
</script>

<style scoped>
.report-loader__wrapper {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  background: var(--Background-S1, #fff);
  position: relative;
  overflow: hidden;
  padding: 0 24px;
  padding-top: 33dvh;
}

.report-loader-info__wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.report-loader-info__wrapper::v-deep .pp-blockchain-icon svg {
  width: 48px;
  height: 48px;
}

.report-loader-info__label {
  font-size: var(--Size-3XL, 24px);
  font-weight: 700;
  line-height: var(--Line-Height-3XL, 32px);
  letter-spacing: var(--Letter-Spacing-None, 0px);
}

.report-loader-info__wallet {
  border-radius: 100px;
  background: var(--Basic-100, rgba(9, 21, 64, 0.04));
  padding: 2px 8px;
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.report-loader-check__wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.report-loader-check__equalizer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 600px;
  animation: loadingPulse 2s linear infinite;
}

.report-loader-check__container {
  position: absolute;
  top: 44px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: var(--Letter-Spacing-XS, -0.5px);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.report-loader-check__container svg {
  vertical-align: bottom;
}

.highlight {
  color: var(--Brand-Main, #194bfb);
  font-weight: 600;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease-in-out;
}

.fade-slide-enter {
  opacity: 0;
  transform: translateY(-10px);
  transition-delay: 0.1s;
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
  transition-delay: 0.1s;
}

@media (max-width: 767px) {
  .report-loader-check__equalizer {
    width: 100%;
  }
}

@keyframes loadingPulse {
  0%,
  100% {
    transform: scaleY(0.9);
    opacity: 0.9;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}
</style>
